import { useState, useMemo } from "react";
import { useData } from "@/contexts/DataContext";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { functions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { useEffect } from "react";
import { collection, query, getDocs, orderBy, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/hooks/use-auth";
import {
  ChevronLeft,
  Crown,
  Calendar,
  CreditCard,
  Ban,
  PauseCircle,
  PlayCircle,
  ArrowRight,
  History,
  ShieldAlert,
  Loader2,
  Zap,
  CheckCircle2,
  User as UserIcon
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export default function BillingPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const {
    userMode,
    proModeExpiry,
    subscriptionId,
    subscriptionStatus,
    subscriptionPlan,
    subscriptionCurrentPeriodEnd,
    openProUpgradeModal,
    isUltimateOwnerMobile,
  } = useData();

  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [cancelType, setCancelType] = useState<'immediate' | 'cycle_end'>('cycle_end');
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) return;
      try {
        // 1. Fetch without Firestore orderBy to prevent missing-field filtering
        const q = query(
          collection(db, "users", user.uid, "subscriptions"),
          limit(20)
        );
        const snap = await getDocs(q);
        const history = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // 2. Sort them locally based on whatever date field exists
        history.sort((a: any, b: any) => {
          const dateA = new Date(a.paidAt || a.updatedAt || a.expirySetTo || 0).getTime();
          const dateB = new Date(b.paidAt || b.updatedAt || b.expirySetTo || 0).getTime();
          return dateB - dateA;
        });

        setPaymentHistory(history);
      } catch (err) {
        console.error("Failed to load payment history", err);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [user]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/settings");
    }
  };

  const hasActiveProValidity = useMemo(() => {
    return proModeExpiry && new Date(proModeExpiry) > new Date();
  }, [proModeExpiry]);

  const isRazorpaySub = !!subscriptionId;

  // 🛡️ CRITICAL FIX: Only show controls if the subscription is ACTUALLY running or paused
  // A status of "created", "authenticated", or "cancelled" means there is nothing left to pause/cancel.
  const isControllableSub = isRazorpaySub && (subscriptionStatus === 'active' || subscriptionStatus === 'paused');

  // Backend Calls
  const handleCancelSubscription = async () => {
    setIsProcessing(true);
    try {
      const cancelSub = httpsCallable(functions, 'cancelRazorpaySubscription');
      await cancelSub({
        subscriptionId,
        cancelAtCycleEnd: cancelType === 'cycle_end'
      });

      toast({ title: "Subscription Cancelled", description: "Your cancellation request has been processed." });
      setShowCancelDialog(false);
    } catch (error: any) {
      toast({ title: "Action Failed", description: error.message || "Could not cancel subscription.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTogglePause = async () => {
    setIsProcessing(true);
    try {
      const action = subscriptionStatus === 'paused' ? 'resumeRazorpaySubscription' : 'pauseRazorpaySubscription';
      const toggleSub = httpsCallable(functions, action);
      await toggleSub({ subscriptionId });

      toast({ title: subscriptionStatus === 'paused' ? "Subscription Resumed" : "Subscription Paused" });
      setShowPauseDialog(false);
    } catch (error: any) {
      toast({ title: "Action Failed", description: error.message || "Could not update subscription status.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const renderStatusBadge = () => {
    if (!hasActiveProValidity) {
      return (
        <div className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border border-slate-200 dark:border-slate-700">
          <UserIcon className="w-3 h-3" /> Free Plan
        </div>
      );
    }

    const badgeStyle = "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border";

    if (!isRazorpaySub || subscriptionStatus === 'created') {
      return (
        <div className={`${badgeStyle} bg-amber-50 text-amber-700 border-amber-200`}>
          <Crown className="w-3 h-3 text-amber-600" /> Premium
        </div>
      );
    }

    switch (subscriptionStatus) {
      case 'active':
        return (
          <div className={`${badgeStyle} bg-emerald-50 text-emerald-700 border-emerald-200`}>
            <Crown className="w-3 h-3 text-emerald-600" /> Premium
          </div>
        );
      case 'paused':
        return (
          <div className={`${badgeStyle} bg-amber-50 text-amber-700 border-amber-200`}>
            <PauseCircle className="w-3 h-3 text-amber-600" /> Premium (Paused)
          </div>
        );
      case 'cancelled':
        return (
          <div className={`${badgeStyle} bg-red-50 text-red-700 border-red-200`}>
            <Ban className="w-3 h-3 text-red-600" /> Premium (Ending)
          </div>
        );
      default:
        return (
          <div className={`${badgeStyle} bg-blue-50 text-blue-700 border-blue-200`}>
            Processing
          </div>
        );
    }
  };

  const displayExpiry = hasActiveProValidity
    ? (subscriptionCurrentPeriodEnd
      ? new Date(subscriptionCurrentPeriodEnd).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
      : proModeExpiry
        ? new Date(proModeExpiry).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
        : "N/A")
    : "FOREVER";

  // Ultimate owner gets a Desktop-style "Plan Overview" billing screen.
  if (isUltimateOwnerMobile) {
    const ultimateExpiry = proModeExpiry
      ? new Date(proModeExpiry).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "N/A";
    return (
      <div
        className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 5.5rem)",
        }}
      >
        <div className="px-4 pb-4">
          <div className="flex items-start gap-3 mb-6">
            <button
              onClick={handleBack}
              className="rounded-full p-2 border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 mt-1 shrink-0"
            >
              <ChevronLeft className="h-4 w-4 text-[#0F172A] dark:text-[#EEEAE3]" />
            </button>
            <div className="min-w-0">
              <h1 className="text-3xl font-black uppercase tracking-tighter leading-[0.9] text-[#0F172A] dark:text-[#EEEAE3]">
                PLAN<br /><span className="text-[#8B1A1A]">OVERVIEW</span>
              </h1>
              <p className="text-[11px] text-slate-500 dark:text-[#8A8A92] mt-3 leading-relaxed">
                Manage your active subscriptions, view upcoming renewals, and control your billing preferences.
              </p>
            </div>
          </div>

          {/* Current Plan Card — gradient red, premium */}
          <div className="relative overflow-hidden rounded-[32px] p-7 mb-6 bg-gradient-to-br from-[#8B1A1A] to-[#631212] dark:from-[#8B1A1A] dark:to-[#3a0a0a] shadow-xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/3" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-7">
                <p className="text-[10px] font-black uppercase tracking-widest text-white/60">
                  Current Status
                </p>
                <span className="rounded-full bg-[#D4AF37] px-3 py-1 flex items-center gap-1.5">
                  <Crown className="h-3 w-3 text-[#5A4615]" />
                  <span className="text-[10px] font-black uppercase tracking-wider text-[#5A4615]">
                    Ultimate Member
                  </span>
                </span>
              </div>

              <h3 className="text-3xl font-black tracking-tight mb-2 text-white">
                Ultimate Edition
              </h3>
              <p className="text-sm font-medium text-white/80 leading-relaxed mb-7">
                One-time extension active. Enjoy premium features.
              </p>

              <div className="rounded-2xl p-4 border bg-black/20 border-white/10 backdrop-blur-md flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/60">
                    Valid Until
                  </p>
                  <p className="text-base font-black text-white mt-0.5">{ultimateExpiry}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment History */}
          <div className="rounded-[32px] border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-5">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-3.5 h-3.5 text-[#8B1A1A]" />
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0F172A] dark:text-[#EEEAE3]">
                Payment History
              </p>
            </div>

            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
              </div>
            ) : paymentHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center px-4 rounded-[20px] border-2 border-dashed border-slate-200 dark:border-[#454545]">
                <CreditCard className="w-10 h-10 text-slate-300 dark:text-[#454545] mb-3" />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
                  No recent payments
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {paymentHistory.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-start justify-between p-4 rounded-2xl border border-slate-100 dark:border-[#454545] bg-slate-50/60 dark:bg-[#1A1614]/60"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-white dark:bg-[#1F1B19] border border-slate-200 dark:border-[#454545] flex items-center justify-center shrink-0">
                        <CreditCard className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-bold text-[#0F172A] dark:text-[#EEEAE3] truncate">
                            {payment.planDays
                              ? `${payment.planDays} Days Premium`
                              : payment.isTrial
                              ? "Trial"
                              : "Subscription"}
                          </p>
                          {payment.hasRefund && (
                            <span className="bg-red-50 border border-red-100 text-red-600 text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest shrink-0">
                              Refunded
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                          {new Date(payment.paidAt || payment.updatedAt).toLocaleDateString(
                            "en-IN",
                            { day: "numeric", month: "short", year: "numeric" }
                          )}
                        </p>
                        <p className="text-[10px] text-slate-400 font-mono bg-white dark:bg-[#1F1B19] px-2 py-0.5 rounded-md inline-block border border-slate-200 dark:border-[#454545] truncate max-w-full">
                          ID: {(payment.paymentId || payment.subscriptionId || payment.id).slice(0, 18)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p
                        className={`text-base font-black tracking-tight ${
                          payment.hasRefund
                            ? "text-slate-400 line-through"
                            : "text-emerald-600 dark:text-emerald-400"
                        }`}
                      >
                        ₹{payment.amount || 0}
                      </p>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">
                        {payment.method || "System"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      {/* Header */}
      <header className="px-4 py-4 flex items-center sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-slate-200/50">
        <button
          onClick={handleBack}
          className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform shrink-0"
        >
          <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
        </button>
        <h1 className="ml-4 text-xl font-black text-slate-900 dark:text-white tracking-tight">Manage Billing</h1>
      </header>

      <div className="flex-1 px-4 py-6 pb-24 overflow-y-auto space-y-6">

        {/* [3.1] Active Plan Card */}
        <div className={`relative overflow-hidden rounded-[32px] p-6 shadow-lg border ${hasActiveProValidity ? 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20 border-amber-200 dark:border-amber-800/50' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'}`}>
          {hasActiveProValidity && <Crown className="absolute -top-4 -right-4 w-24 h-24 text-amber-500/10 rotate-12 pointer-events-none" />}

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Current Plan</h2>
              {renderStatusBadge()}
            </div>

            <div className="mb-8">
              <h3 className={`text-3xl font-black tracking-tight ${hasActiveProValidity ? 'text-amber-900 dark:text-amber-400' : 'text-slate-900 dark:text-white'}`}>
                {hasActiveProValidity ? (subscriptionPlan || "Premium") : "Free Plan"}
              </h3>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">
                {isControllableSub
                  ? "Auto-renews to keep your business running."
                  : hasActiveProValidity
                    ? "One-time extension via Reward Points."
                    : "Basic billing and inventory management."}
              </p>
            </div>

            <div className="bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-2xl p-4 border border-white/50 dark:border-white/5">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasActiveProValidity ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-slate-100 bg-white dark:bg-slate-800'}`}>
                  <Calendar className={`w-5 h-5 ${hasActiveProValidity ? 'text-amber-600 dark:text-amber-500' : 'text-slate-500'}`} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    {isControllableSub ? "Next Auto-Charge Date" : "Valid Until"}
                  </p>
                  <p className="text-sm font-black text-slate-900 dark:text-white">
                    {isControllableSub && subscriptionCurrentPeriodEnd
                      ? new Date(subscriptionCurrentPeriodEnd).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                      : displayExpiry}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🛡️ CRITICAL FIX: Only show Subscription Controls if the sub is Active or Paused */}
        {isControllableSub && (
          <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden divide-y divide-slate-50 dark:divide-slate-800/50">
            <div className="p-4">
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-2">Subscription Controls</h3>

              <button
                onClick={() => subscriptionStatus === 'paused' ? handleTogglePause() : setShowPauseDialog(true)}
                disabled={isProcessing}
                className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                    {subscriptionStatus === 'paused' ? <PlayCircle className="w-5 h-5 text-amber-600" /> : <PauseCircle className="w-5 h-5 text-amber-600" />}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{subscriptionStatus === 'paused' ? "Resume Subscription" : "Pause Subscription"}</p>
                    <p className="text-[10px] font-medium text-slate-500">Temporarily halt billing</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300" />
              </button>

              <button
                onClick={() => setShowCancelDialog(true)}
                disabled={isProcessing}
                className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors mt-1 disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                    <Ban className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-red-600 dark:text-red-400">Cancel Subscription</p>
                    <p className="text-[10px] font-medium text-red-500/70">Stop future automatic payments</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-red-300" />
              </button>
            </div>
          </div>
        )}

        {/* [3.2] Universal Actions — hidden for Ultimate (top-tier, no upgrades) */}
        {!isUltimateOwnerMobile && (
          <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
            <div className="p-4">
              <button
                onClick={openProUpgradeModal}
                className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{hasActiveProValidity ? "Change Plan or Extend" : "Go Premium"}</p>
                    <p className="text-[10px] font-medium text-slate-500">View available pricing plans</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300" />
              </button>
            </div>
          </div>
        )}

        {/* [3.3] Billing History */}
        <div className="pt-4 px-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-400" />
              <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Recent Payments</h3>
            </div>
          </div>

          {isLoadingHistory ? (
            <div className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-100 dark:border-slate-800 p-8 text-center shadow-sm flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-100 dark:border-slate-800 p-8 text-center shadow-sm">
              <CreditCard className="w-8 h-8 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No recent payments</p>
              <p className="text-[10px] text-slate-400 mt-1">Your payment history will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentHistory.map((payment) => (
                <div key={payment.id} className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-100 dark:border-slate-800 p-4 flex items-center justify-between shadow-sm">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">
                        {payment.planDays ? `${payment.planDays} Days Pro` : (payment.isTrial ? "7-Day Trial" : "Subscription")}
                      </p>
                      {payment.hasRefund && (
                        <span className="bg-red-50 text-red-600 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider">Refunded</span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-widest">
                      {new Date(payment.paidAt || payment.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                    <p className="text-[9px] text-slate-300 font-mono mt-1">{payment.paymentId || payment.subscriptionId}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-black tracking-tight ${payment.hasRefund ? 'text-slate-400 line-through' : 'text-emerald-600 dark:text-emerald-400'}`}>
                      ₹{payment.amount || 0}
                    </p>
                    <p className="text-[9px] text-slate-400 uppercase tracking-widest mt-0.5">{payment.method || "System"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* [4.1] Cancellation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="rounded-[32px] border-none p-8 max-w-[340px] bg-[#FFFDF5] dark:bg-slate-900 shadow-2xl z-[400]">
          <AlertDialogHeader>
            <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4 border border-red-100 dark:border-red-800">
              <ShieldAlert className="w-8 h-8 text-[#8B1A1A] dark:text-red-400" />
            </div>
            <AlertDialogTitle className="text-2xl font-black text-center text-slate-900 dark:text-white tracking-tight">
              Cancel Subscription?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center pt-2 text-slate-500 dark:text-slate-400 leading-relaxed">
              How would you like to proceed with your cancellation?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex flex-col gap-3 mt-4">
            <button
              onClick={() => setCancelType('cycle_end')}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${cancelType === 'cycle_end' ? 'border-[#8B1A1A] bg-red-50/50 dark:bg-red-900/10' : 'border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900'}`}
            >
              <p className={`text-sm font-black ${cancelType === 'cycle_end' ? 'text-[#8B1A1A] dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>Cancel at End of Cycle</p>
              <p className="text-[10px] font-medium text-slate-500 mt-1">Keep Pro access until {displayExpiry}. No future charges.</p>
            </button>

            <button
              onClick={() => setCancelType('immediate')}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${cancelType === 'immediate' ? 'border-[#8B1A1A] bg-red-50/50 dark:bg-red-900/10' : 'border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900'}`}
            >
              <p className={`text-sm font-black ${cancelType === 'immediate' ? 'text-[#8B1A1A] dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>Cancel Immediately</p>
              <p className="text-[10px] font-medium text-slate-500 mt-1">Lose Pro access right now. Subscription ends instantly.</p>
            </button>
          </div>

          <AlertDialogFooter className="flex-col gap-3 justify-center mt-6">
            <Button
              disabled={isProcessing}
              className="w-full rounded-2xl h-14 bg-[#8B1A1A] hover:bg-[#631212] text-white font-black uppercase tracking-widest text-[11px] shadow-lg shadow-red-900/20 flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={handleCancelSubscription}
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Cancellation"}
            </Button>
            <AlertDialogCancel disabled={isProcessing} className="w-full rounded-2xl h-14 font-bold border-slate-200 dark:border-slate-700 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 mt-0 border-2">
              Go Back
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* [4.2] Pause Confirmation Dialog */}
      <AlertDialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <AlertDialogContent className="rounded-[32px] border-none p-8 max-w-[340px] bg-[#FFFDF5] dark:bg-slate-900 shadow-2xl z-[400]">
          <AlertDialogHeader>
            <div className="mx-auto w-16 h-16 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4 border border-amber-100 dark:border-amber-800">
              <PauseCircle className="w-8 h-8 text-amber-600 dark:text-amber-500" />
            </div>
            <AlertDialogTitle className="text-2xl font-black text-center text-slate-900 dark:text-white tracking-tight">
              Pause Subscription?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center pt-2 text-slate-500 dark:text-slate-400 leading-relaxed">
              Your subscription will pause at the end of the current billing cycle. You won't be charged again until you resume, but you will lose Pro access while paused.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-3 justify-center mt-6">
            <Button
              disabled={isProcessing}
              className="w-full rounded-2xl h-14 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase tracking-widest text-[11px] shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={handleTogglePause}
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, Pause It"}
            </Button>
            <AlertDialogCancel disabled={isProcessing} className="w-full rounded-2xl h-14 font-bold border-slate-200 dark:border-slate-700 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 mt-0 border-2">
              Keep Subscription
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}